// Search-state for the scheduler route. The route in scheduler.routes.tsx owns
// the validateSearch schema and stripSearchParams middleware; this hook is a
// thin, typed surface over TanStack's useSearch / useNavigate so the page
// doesn't touch the router primitives directly. Date, search, sort and filter
// changes reset page so a previous view's pagination never bleeds into the new one.

import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { DEFAULT_PAGE_SIZE } from '@components/DataTable/constants';
import { EVENT_FORMAT_VALUES } from '@components/EventManagement/types';
import { PRODUCT_LINE_LABELS } from '@components/common/ProductLinePicker/productLines';
import {
  SCHEDULER_ASSIGNEE_TYPE_VALUES,
  SCHEDULER_ASSIGNMENT_STATUS_VALUES,
  SCHEDULER_DRIVER_CHANNELS,
  SCHEDULER_ORDER_STATUS_VALUES,
  SCHEDULER_TAG_VALUES,
} from '@components/Scheduler/types';
import {
  createResourceSearchParser,
  type ResourceListSearchParams,
  type ResourceSortDirection,
} from '@hooks/resourceListPage';

export const SCHEDULER_SORT_COLUMNS = ['event_id', 'pickup_at', 'guest_count'] as const;

export type SchedulerSortColumn = (typeof SCHEDULER_SORT_COLUMNS)[number];

export interface SchedulerSearchParams extends ResourceListSearchParams<SchedulerSortColumn> {
  // ISO date string (YYYY-MM-DD) for the selected day, empty string for today.
  date: string;
  orderStatuses: string[];
  formats: string[];
  productLines: string[];
  // Set only by the header's Unassigned badge: entries nobody is on.
  assignmentStatuses: string[];
  assigneeStatuses: string[];
  assigneeTypes: string[];
  driverStatuses: string[];
  driverTypes: string[];
  tags: string[];
}

export const SCHEDULER_SEARCH_DEFAULTS: SchedulerSearchParams = {
  q: '',
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  sort: '',
  direction: '',
  date: '',
  orderStatuses: ['confirmed'],
  formats: [],
  productLines: [],
  assignmentStatuses: [],
  assigneeStatuses: [],
  assigneeTypes: [],
  driverStatuses: [],
  driverTypes: [],
  tags: [],
};

export type SchedulerFilters = Pick<
  SchedulerSearchParams,
  | 'q'
  | 'orderStatuses'
  | 'formats'
  | 'productLines'
  | 'assignmentStatuses'
  | 'assigneeStatuses'
  | 'assigneeTypes'
  | 'driverStatuses'
  | 'driverTypes'
  | 'tags'
>;

const NO_SCHEDULER_FILTERS: SchedulerFilters = {
  q: '',
  orderStatuses: [],
  formats: [],
  productLines: [],
  assignmentStatuses: [],
  assigneeStatuses: [],
  assigneeTypes: [],
  driverStatuses: [],
  driverTypes: [],
  tags: [],
};

export function hasActiveSchedulerFilters(search: SchedulerFilters): boolean {
  return (Object.keys(NO_SCHEDULER_FILTERS) as (keyof SchedulerFilters)[]).some((key) => search[key].length > 0);
}

const SCHEDULER_ROUTE = '/scheduler' as const;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const VALID_ORDER_STATUSES: ReadonlySet<string> = new Set(SCHEDULER_ORDER_STATUS_VALUES);
const VALID_FORMATS: ReadonlySet<string> = new Set(EVENT_FORMAT_VALUES);
const VALID_PRODUCT_LINES: ReadonlySet<string> = new Set(Object.keys(PRODUCT_LINE_LABELS));
const VALID_ASSIGNMENT_STATUSES: ReadonlySet<string> = new Set(SCHEDULER_ASSIGNMENT_STATUS_VALUES);
const VALID_BADGE_STATUSES: ReadonlySet<string> = new Set(['unassigned']);
const VALID_ASSIGNEE_TYPES: ReadonlySet<string> = new Set(SCHEDULER_ASSIGNEE_TYPE_VALUES);
const VALID_DRIVER_TYPES: ReadonlySet<string> = new Set(SCHEDULER_DRIVER_CHANNELS);
const VALID_TAGS: ReadonlySet<string> = new Set(SCHEDULER_TAG_VALUES);

// Accepts the array TanStack writes and a comma-separated string from a
// hand-typed URL, keeping only the allowed values.
function toAllowedArray(raw: unknown, allowed: ReadonlySet<string>): string[] {
  const values = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : [];
  return values.filter((value): value is string => typeof value === 'string' && allowed.has(value));
}

const parseListSearch = createResourceSearchParser<SchedulerSortColumn>({
  defaults: SCHEDULER_SEARCH_DEFAULTS,
  validSorts: SCHEDULER_SORT_COLUMNS,
  requireSortForDirection: true,
});

// validateSearch handler — coerces unknown URL input into the typed shape.
export function parseSchedulerSearch(raw: Record<string, unknown>): SchedulerSearchParams {
  const date =
    typeof raw.date === 'string' && ISO_DATE_PATTERN.test(raw.date)
      ? raw.date
      : SCHEDULER_SEARCH_DEFAULTS.date;
  return {
    ...parseListSearch(raw),
    date,
    orderStatuses:
      raw.orderStatuses === undefined
        ? SCHEDULER_SEARCH_DEFAULTS.orderStatuses
        : toAllowedArray(raw.orderStatuses, VALID_ORDER_STATUSES),
    formats: toAllowedArray(raw.formats, VALID_FORMATS),
    productLines: toAllowedArray(raw.productLines, VALID_PRODUCT_LINES),
    assignmentStatuses: toAllowedArray(raw.assignmentStatuses, VALID_BADGE_STATUSES),
    assigneeStatuses: toAllowedArray(raw.assigneeStatuses, VALID_ASSIGNMENT_STATUSES),
    assigneeTypes: toAllowedArray(raw.assigneeTypes, VALID_ASSIGNEE_TYPES),
    driverStatuses: toAllowedArray(raw.driverStatuses, VALID_ASSIGNMENT_STATUSES),
    driverTypes: toAllowedArray(raw.driverTypes, VALID_DRIVER_TYPES),
    tags: toAllowedArray(raw.tags, VALID_TAGS),
  };
}

export function useSchedulerRouterSearch() {
  const search = useSearch({ from: SCHEDULER_ROUTE });
  const tanNavigate = useNavigate({ from: SCHEDULER_ROUTE });

  const navigate = useCallback(
    (updates: Partial<SchedulerSearchParams>) => {
      tanNavigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [tanNavigate],
  );

  // Setters depend on navigate alone, so they keep their identity across URL
  // changes and don't invalidate memos keyed on them.
  const setters = useMemo(
    () => ({
      navigate,
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
      setSort: (sort: SchedulerSortColumn | '', direction: ResourceSortDirection) =>
        navigate({ sort, direction, page: 1 }),
      setDate: (date: string) => navigate({ date, page: 1 }),
      setOrderStatuses: (orderStatuses: string[]) => navigate({ orderStatuses, page: 1 }),
      setFormats: (formats: string[]) => navigate({ formats, page: 1 }),
      setProductLines: (productLines: string[]) => navigate({ productLines, page: 1 }),
      // Reads the URL it replaces rather than the last render, so a quick
      // double click turns the filter back off.
      toggleUnassigned: () =>
        tanNavigate({
          search: (prev) => ({
            ...prev,
            assignmentStatuses: prev.assignmentStatuses.includes('unassigned') ? [] : ['unassigned'],
            page: 1,
          }),
          replace: true,
        }),
      setAssigneeStatuses: (assigneeStatuses: string[]) => navigate({ assigneeStatuses, page: 1 }),
      setAssigneeTypes: (assigneeTypes: string[]) => navigate({ assigneeTypes, page: 1 }),
      setDriverStatuses: (driverStatuses: string[]) => navigate({ driverStatuses, page: 1 }),
      setDriverTypes: (driverTypes: string[]) => navigate({ driverTypes, page: 1 }),
      setTags: (tags: string[]) => navigate({ tags, page: 1 }),
      clearFilters: () => navigate({ ...NO_SCHEDULER_FILTERS, page: 1 }),
    }),
    [navigate, tanNavigate],
  );

  return useMemo(() => ({ search, ...setters }), [search, setters]);
}
